import {
  Box,
  Collapse,
  Button,
  Divider,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import CookieSettings from "./CookieSettings";
import {
  setCookieSettingsDialogState,
  setTermsOfUseDialogState,
} from "../reducers/general";
import { useAppDispatch, useVersion } from "../hooks";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
import TermsOfUse from "./TermsOfUse";
import Changelog from "./Changelog";
import { getResponsivePaddings } from "../utils";
import { getVersionLabel } from "../utils/version";
import theme from "../theme";

const FooterContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    height: "65px",
  },

  width: "100vw",

  backgroundColor: "#fff",
  boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.15)",

  position: "fixed",
  bottom: 0,

  zIndex: 1,
}));

const FooterContent = styled(Box)(({ theme }) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "0 auto",
  padding: "0 48px",

  ...getResponsivePaddings(theme, true),
}));

const VersionContainer = styled(Stack)(() => ({
  "& .MuiTypography-root": {
    margin: "0px",
    textTransform: "none",
    fontFamily: "ABBvoice",
    fontSize: "12px",
    fontWeight: "400",
    lineHeight: "1.2",
    color: "rgb(105, 105, 105)",
  },
}));

const DividerStyled = styled(Divider)(() => ({
  height: "16px",
  width: "1px",
  backgroundColor: "#E0E0E0",
  margin: "0 8px",

  [theme.breakpoints.up("md")]: {
    height: "33px",
    margin: "0 16px",
  },
}));

interface FooterProps { }

const Footer: React.FC<FooterProps> = () => {
  const dispatch = useAppDispatch();
  const [seenCookieSettings] = useLocalStorage("seenCookieSettings", "");

  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const { versions, loading } = useVersion();
  const isResponsive = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down("lg"));

  const handleOpenCookieSettings = () => {
    setIsExpanded(false);
    dispatch(setCookieSettingsDialogState(true));
  };

  const handleOpenTermsOfUse = () => {
    setIsExpanded(false);
    dispatch(setTermsOfUseDialogState(true));
  };

  const handleOpenPrivacyNotice = () => {
    setIsExpanded(false);
    window.open(
      "https://new.abb.com/privacy-notice/external-stakeholders",
      "_blank",
    );
  };

  const handleContactUs = () => {
    setIsExpanded(false);
    navigate("/contact-us");
  };

  useEffect(() => {
    if (!seenCookieSettings) {
      handleOpenCookieSettings();
    }
  }, [seenCookieSettings, dispatch]);


  const versionInformation = (
    <VersionContainer
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content max-content',
        columnGap: 0,
        rowGap: 0,
        alignItems: 'center',
      }}
    >
      <Typography sx={{ textAlign: 'right' }}>UI</Typography>
      <Typography sx={{ mx: 0.5 }}>:</Typography>
      <Typography>{getVersionLabel(versions?.ui)}</Typography>

      <Typography sx={{ textAlign: 'right' }}>API</Typography>
      <Typography sx={{ mx: 0.5 }}>:</Typography>
      <Typography>{getVersionLabel(versions?.api)}</Typography>

      <Typography sx={{ textAlign: 'right' }}>MOD</Typography>
      <Typography sx={{ mx: 0.5 }}>:</Typography>
      <Typography>
        {versions?.package?.publishedAtUtc
          ? new Date(versions.package.publishedAtUtc)
            .toISOString()
            .slice(0, 16)
            .replace(/[-:T]/g, '')
          : 'Unavailable'}
      </Typography>
    </VersionContainer>
  );



  return (
    <Box component="footer">
      <CookieSettings />
      <TermsOfUse />

      <FooterContainer>
        <FooterContent>
          {isResponsive ? (
            <Stack
              direction="column"
              alignItems="flex-start"
              gap={isExpanded ? "8px" : "0px"}
              sx={{ width: "100%" }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography
                  variant="bodySmallWeightRegular"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  ©ABB Critical Power Configurator {new Date().getFullYear()}
                </Typography>
                <IconButton
                  sx={{ padding: 0, minWidth: "auto" }}
                  onClick={() => setIsExpanded((prev) => !prev)}
                >
                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                </IconButton>
              </Stack>
              <Collapse in={isExpanded} sx={{ width: "100%", pb: "4px" }}>
                <Stack direction="row" gap="12px" sx={{ width: "100%" }}>
                  <Stack direction="column" alignItems="flex-start" gap="8px">
                    <Button
                      sx={{ padding: 0 }}
                      variant="text"
                      onClick={handleOpenPrivacyNotice}
                    >
                      <Typography
                        variant="bodySmallWeightRegular"
                        sx={(theme) => ({ color: theme.palette.common.black })}
                      >
                        Privacy notice
                      </Typography>
                    </Button>

                    <Button
                      sx={{ padding: 0 }}
                      variant="text"
                      onClick={handleOpenTermsOfUse}
                    >
                      <Typography
                        variant="bodySmallWeightRegular"
                        sx={(theme) => ({ color: theme.palette.common.black })}
                      >
                        Terms of Use
                      </Typography>
                    </Button>
                  </Stack>
                  <Stack direction="column" alignItems="flex-start" gap="8px">
                    <Changelog onOpen={() => setIsExpanded(false)} />

                    <Button
                      sx={{ padding: 0 }}
                      variant="text"
                      onClick={handleOpenCookieSettings}
                    >
                      <Typography
                        variant="bodySmallWeightRegular"
                        sx={(theme) => ({ color: theme.palette.common.black })}
                      >
                        Cookie settings
                      </Typography>
                    </Button>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  sx={{ width: "100%", mt: '12px' }}
                >
                  <Typography
                    variant="bodySmallWeightRegular"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {loading ? "Version loading..." : versionInformation}
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    <Typography
                      variant="bodySmallWeightRegular"
                      sx={(theme) => ({
                        color: theme.palette.grey[600],
                      })}
                    >
                      Need help?
                    </Typography>
                    <Button
                      sx={{ padding: 0 }}
                      variant="text"
                      onClick={handleContactUs}
                    >
                      <Typography
                        variant="bodySmallWeightRegular"
                        sx={(theme) => ({
                          color: theme.palette.common.black,
                          paddingLeft: "8px",
                        })}
                      >
                        Contact us
                      </Typography>
                    </Button>
                  </Stack>
                </Stack>
              </Collapse>
            </Stack>
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <Typography
                  variant="bodySmallWeightRegular"
                  sx={(theme) => ({
                    color: theme.palette.grey[600],
                    maxWidth: "118px",
                  })}
                >
                  ©ABB Critical Power Configurator {new Date().getFullYear()}
                </Typography>

                <DividerStyled />

                <Button
                  sx={{ padding: 0 }}
                  variant="text"
                  onClick={handleOpenPrivacyNotice}
                >
                  <Typography
                    variant="bodySmallWeightRegular"
                    sx={(theme) => ({ color: theme.palette.common.black })}
                  >
                    Privacy notice
                  </Typography>
                </Button>
                <DividerStyled />
                <Button
                  sx={{ padding: 0 }}
                  variant="text"
                  onClick={handleOpenTermsOfUse}
                >
                  <Typography
                    variant="bodySmallWeightRegular"
                    sx={(theme) => ({ color: theme.palette.common.black })}
                  >
                    Terms of Use
                  </Typography>
                </Button>
                <DividerStyled />
                <Changelog />
                <DividerStyled />
                <Button
                  sx={{ padding: 0 }}
                  variant="text"
                  onClick={handleOpenCookieSettings}
                >
                  <Typography
                    variant="bodySmallWeightRegular"
                    sx={(theme) => ({ color: theme.palette.common.black })}
                  >
                    Cookie settings
                  </Typography>
                </Button>
              </Stack>
              <Stack
                direction={isSmallDesktop ? "column" : "row"}
                alignItems={isSmallDesktop ? "flex-end" : "center"}
                justifyContent="flex-end"
              >
                <Typography
                  variant="bodySmallWeightRegular"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {loading ? "Version loading..." : versionInformation}
                </Typography>
                {!isSmallDesktop && <DividerStyled />}
                <Stack direction="row">
                  <Typography
                    variant="bodySmallWeightRegular"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    Need help?
                  </Typography>
                  <Button
                    sx={{ padding: 0 }}
                    variant="text"
                    onClick={handleContactUs}
                  >
                    <Typography
                      variant="bodySmallWeightRegular"
                      sx={(theme) => ({
                        color: theme.palette.common.black,
                        paddingLeft: "8px",
                      })}
                    >
                      Contact us
                    </Typography>
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </FooterContent>
      </FooterContainer>
    </Box>
  );
};

export default Footer;
